import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CalendarCheckIcon, CalendarClockIcon, CalendarPlusIcon, HistoryIcon, HourglassIcon, PlaneIcon, SunIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { Employee, Navigate, PtoRequest } from '../../types/hr';
import { TODAY, formatDate, formatDays, formatRange } from '../../utils/format';
import { milestoneDate, nextTier, tierForYears, tierPosting, tierRate } from '../../utils/policy';
import { accrualStart, balanceFor, getPolicy, historyFor, scheduledDays, serviceYears, usedYtd } from '../../utils/timeOff';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { FilterSelect } from '../ui/FilterSelect';
import { TbdBadge } from '../ui/TbdBadge';
interface TimeOffTabProps {
  employee: Employee;
  requests: PtoRequest[];
  onRequest: () => void;
  onNavigate: Navigate;
}
export function TimeOffTab({
  employee,
  requests,
  onRequest,
  onNavigate
}: TimeOffTabProps) {
  const policy = getPolicy(employee.policyId);
  const balance = balanceFor(employee.id);
  const history = useMemo(() => historyFor(employee), [employee]);
  const years = Array.from(new Set(history.map(h => String(h.year))));
  const [year, setYear] = useState(years[0] ?? '2026');
  const [policyFilter, setPolicyFilter] = useState(policy?.name ?? 'All');
  const used = usedYtd(employee);
  const scheduled = scheduledDays(employee.id);
  const isNewHire = employee.policyId === 'newhire';
  const isRegular = employee.policyId === 'regular';
  const accrualDate = accrualStart(employee.hireDate);
  const schedule = policy?.form.schedule ?? [];
  const service = serviceYears(employee);
  const currentTier = schedule.length ? tierForYears(schedule, service) : undefined;
  const upcomingTier = schedule.length ? nextTier(schedule, service) : undefined;
  const todayIso = format(TODAY, 'yyyy-MM-dd');
  const upcoming = [...timeOffEntries.filter(e => e.employeeId === employee.id && e.end >= todayIso).map(e => ({
    id: e.id,
    start: e.start,
    end: e.end,
    days: e.days,
    label: e.policyLabel,
    pending: false
  })), ...requests.filter(r => r.employeeId === employee.id && (r.status === 'pending' || r.status === 'approved')).map(r => ({
    id: r.id,
    start: r.start,
    end: r.end,
    days: r.days,
    label: getPolicy(r.policyId)?.name ?? '',
    pending: r.status === 'pending'
  }))].sort((a, b) => a.start.localeCompare(b.start));
  const rows = history.filter(h => String(h.year) === year);
  return <div className="space-y-6" data-id="element-569" data-mp="2nc0ybuuxhwj3">
      {policy && balance ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-570" data-mp="2savj32cghv5u">
          <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04] xl:col-span-2" data-id="element-571" data-mp="2wc7andew4qgo">
            <div className="absolute right-0 top-0 h-full w-1.5 bg-flame" aria-hidden="true" data-id="element-572" data-mp="31aimftoqxb1j" />
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-573" data-mp="po6yzrlyybzh">
              <div data-id="element-574" data-mp="kp3ss3u95zpx">
                <div className="flex items-center gap-2" data-id="element-575" data-mp="kokjtcmkylo1">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-azure/10 text-azure" data-id="element-576" data-mp="kr6qla1ncx57">
                    <SunIcon className="h-4 w-4" data-id="element-577" data-mp="bo5orljtds7c" />
                  </span>
                  <button type="button" onClick={() => onNavigate({
                page: 'policy',
                policyId: policy.id
              })} className="text-sm font-semibold text-navy hover:text-azure" data-id="element-578" data-mp="bq8nctz18tno">
                    {policy.name}
                  </button>
                  {policy.hasTbd && <TbdBadge data-id="element-579" data-mp="zlb9gu6gcj0g" />}
                </div>
                <p className="mt-5 flex items-baseline gap-2" data-id="element-580" data-mp="zk06gwktzfae">
                  <span className="text-5xl font-semibold tracking-tight text-navy" data-id="element-581" data-mp="um8g46zu418b">{balance.available}</span>
                  <span className="text-lg font-medium text-slate-500" data-id="element-582" data-mp="1s3yru1u2djxu">days</span>
                </p>
                <p className="mt-1 text-sm text-muted" data-id="element-583" data-mp="1na4ctbpelt53">Available balance</p>
              </div>
              <Button variant="primary" icon={CalendarPlusIcon} onClick={onRequest} data-id="element-584" data-mp="16umr1pbulrb7">
                Request time off
              </Button>
            </div>
            <dl className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-5" data-id="element-585" data-mp="xw1c045l4uf6">
              <div data-id="element-586" data-mp="12ub7ygz4vz1u">
                <dt className="text-xs text-muted" data-id="element-587" data-mp="12tt3ix2bieiu">Used YTD</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-588" data-mp="12wfk1m76s4d7">{formatDays(used)}</dd>
              </div>
              <div data-id="element-589" data-mp="1ulr0ssraai0i">
                <dt className="text-xs text-muted" data-id="element-590" data-mp="1um0jrwdwvowp">Scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-591" data-mp="1unldxkwbd5u1">{formatDays(scheduled)}</dd>
              </div>
              <div data-id="element-592" data-mp="1lt72xuitghgq">
                <dt className="text-xs text-muted" data-id="element-593" data-mp="1lsnhi3tyjaxy">After scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-594" data-mp="29j19xvggfbt2">{formatDays(balance.available - scheduled)}</dd>
              </div>
            </dl>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-1" data-id="element-595" data-mp="2eb2m7hdmh2j3">
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-596" data-mp="2nn0c9p1elc1t">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-597" data-mp="2jm6l8msnzh4p">
                {isNewHire ? <HourglassIcon className="h-4 w-4 text-flame" data-id="element-598" data-mp="2xduzavmh4fob" /> : <CalendarClockIcon className="h-4 w-4 text-azure" data-id="element-599" data-mp="2sfjp2js9o35k" />}
                {isNewHire ? 'Waiting period' : 'Accrual'}
              </div>
              {isNewHire ? <p className="mt-2 text-lg font-semibold text-navy" data-id="element-600" data-mp="24rimdrhrrgy2">Begins {format(accrualDate, 'MMM d')}</p> : currentTier ? <p className="mt-2 text-lg font-semibold text-navy" data-id="element-601" data-mp="2eo54i8ipntwe">{tierRate(currentTier)}</p> : <div className="mt-2 flex items-center gap-2" data-id="element-602" data-mp="29o0nmhdw4x5n">
                  <span className="text-lg font-semibold text-navy" data-id="element-603" data-mp="3q2s724t26u2t">Rate</span>
                  <TbdBadge data-id="element-604" data-mp="3q4v77hnxe3l9" />
                </div>}
              <p className="mt-0.5 text-xs text-muted" data-id="element-605" data-mp="3l3finfmjhey4">
                {isNewHire ? '1 day / month after 30 days, prorated' : currentTier ? `${service} ${service === 1 ? 'year' : 'years'} of service · posted ${tierPosting(currentTier)}` : 'No accrual schedule set'}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-606" data-mp="axz2xlgg788e">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-607" data-mp="39hfichcwn3ko">
                <CalendarCheckIcon className="h-4 w-4 text-deep" data-id="element-608" data-mp="3ef7fpqwbjhw9" />
                {isNewHire ? 'First anniversary' : 'Next milestone'}
              </div>
              {isNewHire || !schedule.length ? <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-609" data-mp="3o6bwmw5k1ctq">{formatDate(employee.hireDate, 'MMM d')}</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-610" data-mp="2qpwde4zblfg0">{isNewHire ? 'Balance tops up to 15 days' : 'Work anniversary'}</p>
                </> : upcomingTier ? <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-611" data-mp="2o4ypjjqh1fp0">{format(milestoneDate(employee.hireDate, upcomingTier), 'MMM d, yyyy')}</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-612" data-mp="2e7alg36z6vu6">
                    Steps up to {upcomingTier.days || 'TBD'} days / yr at {upcomingTier.afterYears} years
                  </p>
                </> : <>
                  <p className="mt-2 text-lg font-semibold text-navy" data-id="element-613" data-mp="258fbcg0t91ll">Top tier reached</p>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-614" data-mp="20fdvo5ss4y0y">Highest accrual on the schedule</p>
                </>}
            </div>
          </div>
        </div> : <Card title="Time off balance" icon={SunIcon} data-id="element-615" data-mp="1qj12d915cm6q">
          <EmptyState icon={SunIcon} title="No time off policy assigned" description="Regular PTO currently covers the Operations pilot group, and new hires are enrolled in New Hire PTO." action={<Button size="sm" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'policies'
      })} data-id="element-617" data-mp="35f0wggh867iu">
                View policies
              </Button>} data-id="element-616" data-mp="1qg55zp4y65ke" />
        </Card>}

      <Card title="Upcoming time off" icon={PlaneIcon} subtitle={upcoming.length ? `${upcoming.length} scheduled` : undefined} bodyClassName="px-3 pb-3" data-id="element-618" data-mp="2ves0n3r5ao3z">
        {upcoming.length === 0 ? <div className="px-3 pb-3" data-id="element-619" data-mp="2mn89b2ou7gj1">
            <EmptyState icon={PlaneIcon} title="Nothing scheduled" compact data-id="element-620" data-mp="2mm6r8e9eltrt" />
          </div> : <ul className="space-y-0.5" data-id="element-621" data-mp="2rgb3nd1fuu0a">
            {upcoming.map(u => <li key={u.id} className="flex items-center gap-4 rounded-xl px-3 py-3 hover:bg-canvas/70" data-id="element-622" data-mp="2cqvdnz9qesln">
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-navy/[0.05] text-navy" data-id="element-623" data-mp="2hooj2q37nikv">
                  <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-624" data-mp="27q7nfftbufsh">{formatDate(u.start, 'MMM')}</span>
                  <span className="text-lg font-semibold leading-tight" data-id="element-625" data-mp="1p1f2pxmltjnm">{formatDate(u.start, 'd')}</span>
                </div>
                <div className="min-w-0 flex-1" data-id="element-626" data-mp="1k45f8nirzi2f">
                  <p className="text-sm font-medium text-navy" data-id="element-627" data-mp="1y2lkea0zp0ig">{formatRange(u.start, u.end)}</p>
                  <p className="text-xs text-muted" data-id="element-628" data-mp="1t3ieyoh3ks9y">
                    {u.label} · {formatDays(u.days)}
                  </p>
                </div>
                <Badge tone={u.pending ? 'orange' : 'green'} dot data-id="element-629" data-mp="27zhwsqu0e6sa">
                  {u.pending ? 'Pending' : 'Approved'}
                </Badge>
              </li>)}
          </ul>}
      </Card>

      <Card title="History" icon={HistoryIcon} bodyClassName="pb-2" action={history.length > 0 && <div className="flex gap-2" data-id="element-631" data-mp="2qr0y8t8dxcs7">
              <FilterSelect label="Policy" value={policyFilter} options={[policy?.name ?? 'All']} onChange={setPolicyFilter} className="w-40" data-id="element-632" data-mp="2qs2b938yo9ck" />
              <FilterSelect label="Year" value={year} options={years} onChange={setYear} className="w-24" data-id="element-633" data-mp="2e9xpbvsgt8x2" />
            </div>} data-id="element-630" data-mp="2gsl7wsh6m7jr">
        {isRegular && currentTier && <p className="mx-6 mb-3 rounded-xl bg-azure/[0.06] px-4 py-2.5 text-xs text-slate-600 ring-1 ring-inset ring-azure/15" data-id="element-634" data-mp="29heaywlye9r7">
            Regular PTO grants the yearly amount for the employee's tier on {tierPosting(currentTier).replace(' each year', '')}; up to {currentTier.carryover || 0} unused days carry over at year end.
          </p>}
        {rows.length === 0 ? <div className="px-6 pb-4" data-id="element-635" data-mp="2t53w4kzdxbrg">
            <EmptyState icon={HistoryIcon} title="No history yet" compact data-id="element-636" data-mp="2o7lwxmb66awn" />
          </div> : <div className="overflow-x-auto" data-id="element-637" data-mp="2j9k62f7i57am">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-638" data-mp="2j8sk7w3pinxj">
              <thead data-id="element-639" data-mp="1ctzvb1o4igu8">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-640" data-mp="1cu9pz2k45c69">
                  <th scope="col" className="px-6 py-2.5" data-id="element-641" data-mp="xpymqfm2hnm5">Date</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-642" data-mp="xrrqnn5aptqg">Description</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-643" data-mp="12ptawbegjz3i">Used (-)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-644" data-mp="17nus0qsbpocc">Earned (+)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-645" data-mp="1cel0wdzyk9bs">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-646" data-mp="1gfem2zm0na0z">
                {rows.map(h => <tr key={h.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-647" data-mp="1lehsandqfmaj">
                    <td className="whitespace-nowrap px-6 py-3 text-slate-600" data-id="element-648" data-mp="1qe2oiq1ipgjk">{formatDate(h.date, 'MM/dd/yyyy')}</td>
                    <td className="px-6 py-3 text-slate-800" data-id="element-649" data-mp="1vcntf2rpsoec">{h.description}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-rose-600" data-id="element-650" data-mp="3s6io7y3xib0t">{h.used ? `-${h.used.toFixed(2)}` : ''}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-emerald-600" data-id="element-651" data-mp="10h8hc90qt8u">
                      {h.pendingAccrual ? <TbdBadge data-id="element-652" data-mp="3p4w5kz0dt785" /> : h.earned ? `+${h.earned.toFixed(2)}` : ''}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold tabular-nums text-navy" data-id="element-653" data-mp="3k7469jxeqitx">{h.balance.toFixed(2)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>
    </div>;
}